import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Eye, Loader2, RefreshCw, Feather, ExternalLink, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { openBlobInNewTab, downloadBlob } from '../../utils/fileUtils';
import { PhotoAncien, ArchiveBadge, DOC_LABELS, formatDateFr, formatTaille } from './shared';

const BC_STATUTS = { valide: 'Validé', rattrapage: 'Validé (rattrapage)', equivalence: 'Équivalence', non_valide: 'Non validé', en_cours: 'En cours' };

const Info = ({ label, value, testId, wide = false }) => (
  <div className={wide ? 'col-span-2 sm:col-span-1' : ''}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-sm font-medium text-[#0F172A] break-words" data-testid={testId}>{value || '—'}</p>
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{title}</h3>
    {children}
  </div>
);

const DocumentsPrerequis = ({ docs }) => {
  const voir = async (doc) => {
    try {
      const res = await axios.get(`/candidats/doc-view/${doc.document_id}`, { responseType: 'blob' });
      openBlobInNewTab(res.data);
    } catch (e) {
      toast.error("Impossible d'ouvrir ce document");
    }
  };
  if (!docs.length) return <p className="text-sm text-slate-500">Aucun document de candidature conservé</p>;
  return (
    <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg" data-testid="ancien-documents-prerequis">
      {docs.map((d) => (
        <div key={d.document_id} className="flex items-center justify-between gap-2 px-3 py-2">
          <div className="min-w-0 flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#8DC63F] shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#0F172A] truncate">{DOC_LABELS[d.type_document] || d.type_document}</p>
              <p className="text-xs text-slate-500 truncate">{d.nom_fichier} · {formatDateFr(d.date)}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => voir(d)} className="text-[#8DC63F] hover:bg-green-50" data-testid={`btn-voir-prerequis-${d.document_id}`}>
            <Eye className="h-4 w-4 mr-1" /> Voir
          </Button>
        </div>
      ))}
    </div>
  );
};

const ArchiveSection = ({ fiche, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const { archive } = fiche;

  const telecharger = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`/anciens-stagiaires/${fiche.user_id}/archive`, { responseType: 'blob' });
      downloadBlob(res.data, `Archive_fin_formation_${fiche.nom}_${fiche.prenom}.zip`);
    } catch (e) {
      toast.error("Impossible de télécharger l'archive");
    } finally {
      setBusy(false);
    }
  };

  const regenerer = async () => {
    try {
      await axios.post(`/anciens-stagiaires/${fiche.user_id}/archive`);
      toast.success("Création de l'archive lancée");
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de lancer la création de l'archive");
    }
  };

  const alleger = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`/anciens-stagiaires/${fiche.user_id}/alleger`);
      toast.success(`Dossier allégé : ${formatTaille(data.octets_liberes)} libérés`);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'alléger ce dossier");
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="ancien-archive-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <ArchiveBadge statut={archive.statut} allege={!!fiche.allege_le} />
        <span className="text-xs text-slate-500">{archive.date ? `Figée le ${formatDateFr(archive.date)} · ${formatTaille(archive.taille)}` : ''}</span>
      </div>
      {archive.statut === 'erreur' && <p className="text-xs text-red-600">{archive.erreur}</p>}
      {fiche.allege_le && <p className="text-xs text-slate-600" data-testid="ancien-allege-info">Allégé le {formatDateFr(fiche.allege_le)} ({formatTaille(fiche.allege_octets)} libérés). Photo, prérequis et archive conservés.</p>}
      <div className="flex flex-wrap gap-2">
        <Button onClick={telecharger} disabled={busy || archive.statut !== 'pret'} className="bg-[#8DC63F] hover:bg-[#7db336] text-white" data-testid="btn-download-archive-figee">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Archive complète (ZIP)
        </Button>
        {!fiche.allege_le && archive.statut !== 'en_cours' && (
          <Button variant="outline" onClick={regenerer} disabled={busy} data-testid="btn-regenerer-archive">
            <RefreshCw className="h-4 w-4 mr-2" /> {archive.statut === 'pret' ? 'Régénérer' : "Créer l'archive"}
          </Button>
        )}
        {!fiche.allege_le && (
          <Button variant="outline" onClick={() => setConfirm(true)} disabled={busy || archive.statut !== 'pret'} className="border-slate-400" data-testid="btn-alleger-ancien">
            <Feather className="h-4 w-4 mr-2" /> Alléger
          </Button>
        )}
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alléger le dossier de {fiche.prenom} {fiche.nom} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les fichiers de formation seront retirés de la base de données. Ils restent dans l'archive ZIP figée. La photo, les documents de prérequis et l'archive sont conservés. Action définitive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-alleger-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={alleger} data-testid="btn-alleger-confirm">Alléger</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const AncienStagiaireDialog = ({ userId, onClose, onChanged }) => {
  const [fiche, setFiche] = useState(null);

  const charger = useCallback(() => {
    axios.get(`/anciens-stagiaires/${userId}`)
      .then(({ data }) => setFiche(data))
      .catch(() => { toast.error('Impossible de charger la fiche'); onClose(); });
  }, [userId, onClose]);

  useEffect(() => { setFiche(null); charger(); }, [charger]);

  useEffect(() => {
    if (fiche?.archive?.statut !== 'en_cours') return undefined;
    const t = setInterval(charger, 4000);
    return () => clearInterval(t);
  }, [fiche?.archive?.statut, charger]);

  const refresh = () => { charger(); onChanged(); };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ancien-stagiaire-dialog">
        {!fiche ? (
          <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…</div>
        ) : (
          <div className="space-y-6">
            <DialogHeader>
              <div className="flex items-center gap-4">
                <PhotoAncien userId={fiche.user_id} prenom={fiche.prenom} nom={fiche.nom} className="h-24 w-20 text-2xl" />
                <div className="min-w-0">
                  <DialogTitle className="text-xl" data-testid="ancien-dialog-nom">{fiche.prenom} {fiche.nom?.toUpperCase()}</DialogTitle>
                  <DialogDescription>
                    {fiche.numero_candidat ? `N°${fiche.numero_candidat} · ` : ''}{fiche.centre_formation || '—'} · Moniteur VNM depuis le {formatDateFr(fiche.date_conversion)}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <Section title="Identité">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Info label="Date de naissance" value={formatDateFr(fiche.date_naissance)} testId="ancien-date-naissance" />
                <Info label="Lieu de naissance" value={fiche.lieu_naissance} />
                <Info label="Téléphone" value={fiche.telephone} />
                <Info label="Email" value={fiche.email} wide />
                <Info label="Adresse" value={[fiche.adresse, `${fiche.code_postal || ''} ${fiche.ville || ''}`.trim()].filter(Boolean).join(', ')} testId="ancien-adresse" />
              </div>
            </Section>

            <Section title="Diplôme">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Info label="Diplôme" value={fiche.diplome} />
                <Info label="Année" value={fiche.annee_diplome} />
                <Info label="Carte pro" value={fiche.carte_pro_numero ? `${fiche.carte_pro_numero} (exp. ${formatDateFr(fiche.carte_pro_date_expiration)})` : 'Non renseignée'} />
              </div>
              <div className="flex flex-wrap gap-2 pt-1" data-testid="ancien-bc-list">
                {fiche.blocs_competences.map((b) => (
                  <span key={b.bloc_id} className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {b.bloc_id} : {BC_STATUTS[b.statut] || b.statut}
                  </span>
                ))}
              </div>
            </Section>

            <Section title="Documents de prérequis (candidature)">
              <DocumentsPrerequis docs={fiche.documents_prerequis} />
            </Section>

            <Section title="Archive de fin de formation">
              <ArchiveSection fiche={fiche} onChange={refresh} />
            </Section>

            <Link to={`/fiche-stagiaire?id=${fiche.user_id}`} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#8DC63F]" data-testid="ancien-lien-fiche-complete">
              <ExternalLink className="h-4 w-4" /> Ouvrir la fiche stagiaire complète
            </Link>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AncienStagiaireDialog;
